import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Building2, Plus, Trash2, Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Properties = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', address: '' });
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ name: '', address: '' });

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      const { data } = await axios.get(`${API}/properties`, { withCredentials: true });
      // Sort by tower number (natural sort)
      data.sort((a, b) => {
        const aNum = parseInt((a.name.match(/\d+/) || [0])[0], 10);
        const bNum = parseInt((b.name.match(/\d+/) || [0])[0], 10);
        return aNum - bNum;
      });
      setProperties(data);
    } catch (error) {
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.address.trim()) {
      toast.error('Address is required');
      return;
    }
    try {
      await axios.post(`${API}/properties`, formData, { withCredentials: true });
      toast.success('Property created');
      setShowForm(false);
      setFormData({ name: '', address: '' });
      loadProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create property');
    }
  };

  const startEdit = (prop) => {
    setEditingId(prop._id);
    setEditData({
      name: prop.name,
      address: prop.address,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({ name: '', address: '' });
  };

  const saveEdit = async (propId) => {
    if (!editData.address.trim()) {
      toast.error('Address is required');
      return;
    }
    try {
      await axios.patch(`${API}/properties/${propId}`, editData, { withCredentials: true });
      toast.success('Property updated');
      setEditingId(null);
      loadProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update property');
    }
  };

  const handleDelete = async (propId, propName) => {
    if (!window.confirm(`Delete "${propName}"?\n\nSafe delete first — will fail if residents/invoices are still attached.`)) return;
    try {
      const { data } = await axios.delete(`${API}/properties/${propId}`, { withCredentials: true });
      toast.success(data?.message || 'Property deleted');
      loadProperties();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to delete property';
      // If the safe delete was blocked, offer a force delete (cascade cleanup).
      if (error.response?.status === 400 && detail.includes('Cannot delete')) {
        if (window.confirm(
          `${detail}\n\n` +
          `Force delete "${propName}"?\n\n` +
          `This will:\n` +
          `  • Detach all residents from this property\n` +
          `  • DELETE all invoices, bookings, maintenance requests, and parcels tied to it\n\n` +
          `This CANNOT be undone. Type OK in the next prompt to confirm you understand.`
        )) {
          const confirmType = window.prompt('Type OK to confirm force delete:');
          if ((confirmType || '').trim().toUpperCase() !== 'OK') {
            toast.info('Force delete cancelled');
            return;
          }
          try {
            const { data } = await axios.delete(
              `${API}/properties/${propId}?force=true`,
              { withCredentials: true }
            );
            toast.success(
              `Force delete OK. Detached ${data.detached_users} user(s), removed ${data.deleted_invoices} invoice(s), ${data.deleted_bookings} booking(s).`
            );
            loadProperties();
          } catch (e) {
            toast.error(e.response?.data?.detail || 'Force delete failed');
          }
        }
      } else {
        toast.error(detail);
      }
    }
  };

  return (
    <div className="space-y-6" data-testid="properties-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'Outfit' }}>Properties</h1>
          <p className="text-sm text-[#64748b]">Manage all SPAN properties and their addresses ({properties.length} total)</p>
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
          data-testid="add-property-button"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Property
        </Button>
      </div>

      {showForm && (
        <Card className="p-6 bg-white border border-[#e2e8f0]">
          <form onSubmit={handleCreate} className="space-y-4" data-testid="property-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Property Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Span Tower 51"
                  required
                  data-testid="property-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Full address"
                  required
                  data-testid="property-address-input"
                />
              </div>
            </div>
            <p className="text-xs text-[#64748b]">Maintenance fees are set per resident on their user profile. Water tariff is managed centrally in the Water Tariff page.</p>
            <div className="flex gap-3">
              <Button type="submit" className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white" data-testid="submit-property-button">
                Create Property
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563eb] mx-auto"></div>
        </div>
      ) : (
        <Card className="bg-white border border-[#e2e8f0] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                <tr>
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-widest text-[#64748b]">Property</th>
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-widest text-[#64748b]">Address</th>
                  <th className="text-right p-4 text-xs font-semibold uppercase tracking-widest text-[#64748b]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {properties.map((prop, index) => (
                  <tr key={prop._id} className="border-b border-[#e2e8f0] last:border-b-0 hover:bg-[#f8fafc]" data-testid={`property-row-${index}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-md bg-[#2563eb]/10 flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-[#2563eb]" />
                        </div>
                        {editingId === prop._id ? (
                          <Input
                            value={editData.name}
                            onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                            className="max-w-xs"
                            data-testid={`edit-name-${index}`}
                          />
                        ) : (
                          <span className="font-medium text-sm">{prop.name}</span>
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-sm text-[#64748b]">
                      {editingId === prop._id ? (
                        <Input
                          value={editData.address}
                          onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                          data-testid={`edit-address-${index}`}
                        />
                      ) : (
                        prop.address
                      )}
                    </td>
                    <td className="p-4 text-right">
                      {editingId === prop._id ? (
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => saveEdit(prop._id)} className="text-[#10b981]" data-testid={`save-property-${index}`}>
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={cancelEdit} className="text-[#64748b]">
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => startEdit(prop)} className="text-[#2563eb]" data-testid={`edit-property-${index}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(prop._id, prop.name)} className="text-[#ef4444]" data-testid={`delete-property-${index}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};
